import { useCallback } from 'react';
import { isAppSwitch } from '@cbhq/identity-shared/utils';

import { useClientProps } from '../providers/ClientPropsProvider';

function isLogout(key?: string) {
  return key === 'sign_out';
}

function getPathname(url: URL) {
  return url.pathname + url.search;
}

function getUrl(url: string): URL | undefined {
  try {
    return new URL(url, window.location.href);
  } catch (e) {
    // Ignore -- this will only be called in server-side.
  }
  return undefined;
}

type NavigateOptions = {
  method?: 'get' | 'post';
};

// Submits a hidden form to force a real POST navigation -- SameSite=Lax cookies
// are not sent on cross-site POSTs, unlike GET, so this is used for signout to
// prevent CSRF via cross-site top-level navigation.
function postNavigate(targetUrl: URL) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = targetUrl.href;
  document.body.appendChild(form);
  form.submit();
}

export function useNavigate() {
  const { navigate, onBeforeNavigate } = useClientProps();

  return useCallback(
    async (url: string, key?: string, options?: NavigateOptions) => {
      const targetUrl = getUrl(url);

      if (!targetUrl) {
        return undefined;
      }

      await onBeforeNavigate?.(targetUrl);

      if (options?.method === 'post') {
        return postNavigate(targetUrl);
      }

      if (isAppSwitch({ url: targetUrl }) && !isLogout(key)) {
        return window.open(targetUrl, '_blank')?.focus();
      }

      if (!isAppSwitch({ url: targetUrl }) && navigate) {
        return navigate(getPathname(targetUrl));
      }

      window.location.href = targetUrl.href;
      return undefined;
    },
    [navigate, onBeforeNavigate],
  );
}
